import { useState, useEffect, useCallback } from 'react'
import { Table, Button, Space, Input, Modal, Form, Typography, message, Popconfirm, Tag, Tooltip, Tabs } from 'antd'
import { CloudServerOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'
import { listEnvironments, createEnvironment, updateEnvironment, deleteEnvironment } from '@/api/environments'
import { useProjectStore } from '@/stores/project'
import type { Environment, ApiKVItem } from '@/types'

const { Title, Text } = Typography

interface EnvironmentFormValues {
  name: string
  url: string
  description: string
  headerEntries: ApiKVItem[]
  variableEntries: ApiKVItem[]
}

const sectionStyle = {
  background: 'var(--au-header-bg)',
  borderRadius: 8,
  border: '1px solid var(--au-border)',
  padding: '10px 16px',
  marginBottom: 14,
}

const tableHeaderStyle = {
  display: 'grid',
  gridTemplateColumns: '60px 1fr 1fr 1fr 60px',
  gap: 12,
  padding: '8px 4px',
  color: 'var(--au-slate)',
  fontWeight: 500,
  fontSize: 13,
  borderBottom: '1px solid var(--au-border)',
  marginBottom: 8,
}

const kvArrayToJson = (entries: ApiKVItem[]): string => {
  const filtered = entries.filter((e) => e.key.trim() !== '')
  if (filtered.length === 0) return '[]'
  return JSON.stringify(filtered)
}

const jsonToKvArray = (jsonStr: string): ApiKVItem[] => {
  try {
    const parsed = JSON.parse(jsonStr || '[]')
    if (Array.isArray(parsed)) return parsed
    if (typeof parsed === 'object' && parsed !== null) {
      return Object.entries(parsed).map(([key, value]) => ({
        key,
        value: String(value),
        remark: '',
      }))
    }
    return []
  } catch {
    return []
  }
}

const renderKvTags = (jsonStr: string, color: string) => {
  const items = jsonToKvArray(jsonStr)
  if (items.length === 0) return <span style={{ color: 'var(--au-slate-muted)' }}>-</span>
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {items.map((item, idx) => (
        <Tooltip key={idx} title={item.remark || item.value}>
          <Tag color={color} style={{ margin: 0, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.key}{item.value ? `=${item.value}` : ''}
          </Tag>
        </Tooltip>
      ))}
    </div>
  )
}

export default function EnvironmentsPage() {
  const [data, setData] = useState<Environment[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 })
  const [modalOpen, setModalOpen] = useState(false)
  const [editingEnv, setEditingEnv] = useState<Environment | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [form] = Form.useForm<EnvironmentFormValues>()

  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0

  const fetchData = useCallback(async (page = 1, pageSize = 10, keyword = '') => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await listEnvironments({ project_id: projectId, page, page_size: pageSize, keyword })
      setData(res.data)
      setPagination((prev) => ({ ...prev, page, pageSize, total: res.total }))
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleCreate = () => {
    setEditingEnv(null)
    setActiveTab('http')
    form.resetFields()
    form.setFieldsValue({ url: '', description: '', headerEntries: [], variableEntries: [] })
    setModalOpen(true)
  }

  const handleEdit = (record: Environment) => {
    setEditingEnv(record)
    setActiveTab('http')
    form.setFieldsValue({
      name: record.name,
      url: record.url,
      description: record.description,
      headerEntries: jsonToKvArray(record.headers),
      variableEntries: jsonToKvArray(record.variables),
    })
    setModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteEnvironment(id, projectId)
      message.success('删除成功')
      fetchData(pagination.page, pagination.pageSize)
    } catch {
      // 错误已在拦截器处理
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)
      const payload = {
        name: values.name,
        url: values.url || '',
        headers: kvArrayToJson(values.headerEntries || []),
        variables: kvArrayToJson(values.variableEntries || []),
        description: values.description || '',
      }
      if (editingEnv) {
        await updateEnvironment(editingEnv.id, payload, projectId)
        message.success('更新成功')
      } else {
        await createEnvironment({ ...payload, project_id: projectId })
        message.success('创建成功')
      }
      setModalOpen(false)
      fetchData(pagination.page, pagination.pageSize)
    } catch {
      // 表单校验失败或接口错误
    } finally {
      setSubmitting(false)
    }
  }

  const columns: ColumnsType<Environment> = [
    {
      title: '环境名称',
      dataIndex: 'name',
      width: 160,
      render: (name: string) => (
        <Space>
          <CloudServerOutlined style={{ color: 'var(--au-primary)' }} />
          <span style={{ fontWeight: 500 }}>{name}</span>
        </Space>
      ),
    },
    {
      title: 'URL',
      dataIndex: 'url',
      width: 240,
      ellipsis: true,
      render: (v: string) => v ? <Text copyable style={{ fontSize: 14 }}>{v}</Text> : <span style={{ color: 'var(--au-slate-muted)' }}>-</span>,
    },
    {
      title: '请求头',
      dataIndex: 'headers',
      width: 200,
      render: (v: string) => renderKvTags(v, 'green'),
    },
    {
      title: '环境变量',
      dataIndex: 'variables',
      width: 200,
      render: (v: string) => renderKvTags(v, 'blue'),
    },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 200,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定删除此环境？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const renderKvFormList = (name: string, label: string, placeholder: Record<string, string>) => (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <Text strong style={{ fontSize: 14, color: 'var(--au-slate)' }}>{label}</Text>
            <Button
              type="primary"
              size="small"
              onClick={() => add({ key: '', value: '', remark: '' })}
              style={{
                background: 'var(--au-primary)',
                border: 'none',
              }}
            >
              添加
            </Button>
          </div>

          <div style={tableHeaderStyle}>
            <span>序号</span>
            <span>Key</span>
            <span>Value</span>
            <span>Remark</span>
            <span></span>
          </div>

          {fields.length === 0 && (
            <div style={{ color: 'var(--au-slate-muted)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
              暂无数据，点击右上角"添加"按钮新增
            </div>
          )}

          {fields.map(({ key, name: fieldName, ...restField }, index) => (
            <div
              key={key}
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 1fr 1fr 60px',
                gap: 12,
                alignItems: 'start',
                padding: '8px 4px',
                background: index % 2 === 0 ? '#ffffff' : 'var(--au-header-bg)',
                borderRadius: 4,
                marginBottom: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', height: 28, color: 'var(--au-slate-muted)', fontSize: 13 }}>
                {index + 1}
              </div>
              <Form.Item {...restField} name={[fieldName, 'key']} style={{ marginBottom: 0 }}
                rules={[{ required: true, message: '请输入Key' }]}>
                <Input placeholder={placeholder.key} size="small" />
              </Form.Item>
              <Form.Item {...restField} name={[fieldName, 'value']} style={{ marginBottom: 0 }}>
                <Input placeholder={placeholder.value} size="small" />
              </Form.Item>
              <Form.Item {...restField} name={[fieldName, 'remark']} style={{ marginBottom: 0 }}>
                <Input placeholder={placeholder.remark} size="small" />
              </Form.Item>
              <Button type="text" size="small" danger onClick={() => remove(fieldName)} style={{ padding: 0 }}>
                删除
              </Button>
            </div>
          ))}
        </div>
      )}
    </Form.List>
  )

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          环境管理
        </Title>
        <Space>
          <Button onClick={() => fetchData(1, pagination.pageSize)}>
            刷新
          </Button>
          <Button
            type="primary"
            onClick={handleCreate}
            disabled={!projectId}
            style={{
              background: 'var(--au-primary)',
              border: 'none',
            }}
          >
            新增环境
          </Button>
        </Space>
      </div>

      <div className="search-bar">
        <Space.Compact>
          <Input
            placeholder="搜索环境名称"
            allowClear
            onPressEnter={(e) => { const v = (e.target as HTMLInputElement).value; fetchData(1, pagination.pageSize, v) }}
            style={{ minWidth: 200 }}
          />
          <Button
            type="primary"
            onClick={() => {
              const input = document.querySelector('.search-bar input') as HTMLInputElement | null
              const val = input?.value || ''
              fetchData(1, pagination.pageSize, val)
            }}
            style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
          >
            查询
          </Button>
        </Space.Compact>
      </div>

      <div className="content-card">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ y: '100%' }} /* 实际表体高度由 aurora-page--fit 的 flex 布局分配 */
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => fetchData(page, pageSize),
          }}
        />
      </div>

      <Modal
        title={editingEnv ? '编辑环境' : '新增环境'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        destroyOnClose
        width={800}
        wrapClassName="aurora-modal-wrap"
        className="aurora-modal"
        footer={(
          <Space>
            <Button onClick={() => setModalOpen(false)}>取消</Button>
            <Button
              type="primary"
              loading={submitting}
              onClick={handleSubmit}
              style={{
                background: 'var(--au-primary)',
                border: 'none',
              }}
            >
              保存
            </Button>
          </Space>
        )}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 4 }}>
          {/* 基础信息 - 始终在上方显示 */}
          <div style={sectionStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item
                name="name"
                label={<Text strong>环境名称</Text>}
                rules={[{ required: true, message: '请输入环境名称' }]}
              >
                <Input placeholder="如：开发环境" />
              </Form.Item>
              <Form.Item
                name="url"
                label={<Text strong>环境地址</Text>}
                rules={[{ required: true, message: '请输入环境地址' }]}
              >
                <Input placeholder="如：https://api-dev.example.com" />
              </Form.Item>
            </div>
            <Form.Item name="description" label={<Text strong>备注</Text>} style={{ marginBottom: 0 }}>
              <Input.TextArea rows={2} placeholder="请输入备注说明" />
            </Form.Item>
          </div>

          {/* HTTP配置 & 环境变量 - 下方标签页切换 */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            style={{ marginTop: 0 }}
            items={[
              {
                key: 'http',
                label: <span style={{ padding: '0 4px' }}>HTTP配置</span>,
                children: (
                  <div style={sectionStyle}>
                    {renderKvFormList('headerEntries', '通用请求头', { key: 'Header Key', value: 'Header Value', remark: '备注说明' })}
                  </div>
                ),
              },
              {
                key: 'variables',
                label: <span style={{ padding: '0 4px' }}>环境变量</span>,
                children: (
                  <div style={sectionStyle}>
                    {renderKvFormList('variableEntries', '环境变量', { key: '变量名', value: '变量值', remark: '备注说明' })}
                  </div>
                ),
              },
            ]}
          />
        </Form>
      </Modal>
    </div>
  )
}
